import React, { useState, useEffect, useRef } from "react";

interface LiveNumberProps {
  value: number;
  fmt: (n: number) => string;
  /** Couleur vers laquelle le chiffre revient après le flash (défaut : navy). */
  baseColor?: string;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Affiche un nombre formaté qui flashe :
 *  - teal  (#2A9D8F) quand la valeur monte
 *  - rouge (#E05252) quand la valeur descend
 * puis revient en douceur à `baseColor` en 600 ms.
 *
 * Usage :
 *   <LiveNumber value={netMensuel} fmt={fmt} baseColor={C.teal} />
 */
export function LiveNumber({
  value,
  fmt,
  baseColor = "#1B3A5C",
  className = "",
  style = {},
}: LiveNumberProps) {
  const [color, setColor] = useState(baseColor);
  const [scale, setScale] = useState(1);
  const prev = useRef<number | null>(null);

  // Sync baseColor si la prop change dynamiquement (ex: provisionColor dans Dashboard)
  const baseColorRef = useRef(baseColor);
  useEffect(() => {
    baseColorRef.current = baseColor;
    // Mettre à jour la couleur courante uniquement si on n'est pas en train de flasher
    setColor(c => (c === "#2A9D8F" || c === "#E05252" ? c : baseColor));
  }, [baseColor]);

  useEffect(() => {
    if (prev.current === null) { prev.current = value; return; }
    if (prev.current === value) return;
    const up = value > prev.current;
    setColor(up ? "#2A9D8F" : "#E05252");
    setScale(1.08);
    prev.current = value;
    const t = setTimeout(() => {
      setColor(baseColorRef.current);
      setScale(1);
    }, 600);
    return () => clearTimeout(t);
  }, [value]);

  return (
    <span
      className={className}
      style={{
        color,
        transform: `scale(${scale})`,
        display: "inline-block",
        transition: "color 0.45s ease-out, transform 0.15s ease-out",
        ...style,
      }}
    >
      {fmt(value)}
    </span>
  );
}
